import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Row, Spinner } from "react-bootstrap";
import { BriefcaseBusiness, Clock3, MapPin, ShieldCheck, UsersRound } from "lucide-react";
import api from "Common/api";
import "./homeowner-dashboard.css";

const RELIBUILD_LOGO = "https://tradesmen.s3.amazonaws.com/relibuild-logo/relibuild-logo.png";

type RecentContractor = {
  id: string;
  name: string;
  specialty?: string;
  companyName?: string;
  image?: string;
  location?: string;
  availability?: string;
  relatedTo?: { name?: string };
};

type SuggestedContractor = {
  id: string;
  name: string;
  profile: string;
  companyName?: string;
  image?: string;
  location: string;
  agentName: string;
  services: string[];
};

function clean(value: unknown) {
  const text = String(value ?? "").trim();
  return text && text !== "null" && text !== "undefined" ? text : "";
}

function parseLabels(value: unknown): string[] {
  if (!value) return [];
  let parsed: any = value;

  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      parsed = value.split(",");
    }
  }

  return (Array.isArray(parsed) ? parsed : [parsed])
    .map((item: any) => typeof item === "string" ? item : item?.label || item?.value || item?.name)
    .map(clean)
    .filter(Boolean);
}

function mapSuggestedContractor(row: any): SuggestedContractor {
  const profiles = parseLabels(row.ua_profile);
  return {
    id: String(row.acs_contractor_user_id || row.contractor_id || row.id),
    name: `${clean(row.user_first_name)} ${clean(row.user_last_name)}`.trim() || clean(row.cc_company_name) || "Contractor",
    profile: profiles[0] || "General Contractor",
    companyName: clean(row.cc_company_name),
    image: clean(row.ua_profile_pic) || clean(row.cc_logo_url),
    location: [clean(row.ua_city) || clean(row.cc_city), clean(row.ua_state) || clean(row.cc_state)]
      .filter(Boolean)
      .join(", ") || "Location not provided",
    agentName: clean(row.agent_name) || `${clean(row.agent_first_name)} ${clean(row.agent_last_name)}`.trim() || "Your agent",
    services: parseLabels(row.ua_skills),
  };
}

function ContractorAvatar({ image, name }: { image?: string; name: string }) {
  const [failed, setFailed] = useState(false);
  const source = image && !failed ? image : RELIBUILD_LOGO;
  return <img src={source} alt={name} onError={() => setFailed(true)} className={image && !failed ? "is-profile" : "is-logo"} />;
}

export default function ContractorDiscoveryPreview() {
  const [recent, setRecent] = useState<RecentContractor[]>([]);
  const [suggested, setSuggested] = useState<SuggestedContractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [recentFailed, setRecentFailed] = useState(false);
  const [suggestedFailed, setSuggestedFailed] = useState(false);

  useEffect(() => {
    let active = true;

    Promise.allSettled([
      api.get("/jobs/recentContractr"),
      api.post("/agent/suggest-contractors/homeowner", {}),
    ]).then(([recentResult, suggestedResult]) => {
      if (!active) return;

      if (recentResult.status === "fulfilled") {
        const rows = (recentResult.value as any)?.data;
        setRecent(Array.isArray(rows) ? rows.slice(0, 2) : []);
      } else {
        setRecentFailed(true);
      }

      if (suggestedResult.status === "fulfilled") {
        const rows = (suggestedResult.value as any)?.data;
        setSuggested(Array.isArray(rows) ? rows.map(mapSuggestedContractor).slice(0, 2) : []);
      } else {
        setSuggestedFailed(true);
      }

      setLoading(false);
    });

    return () => { active = false; };
  }, []);

  return (
    <div className="homeowner-contractor-previews">
      <section className="homeowner-discovery-section" aria-labelledby="recent-contractors-heading">
        <div className="homeowner-discovery-section__header">
          <div>
            <span className="homeowner-discovery-section__kicker"><Clock3 size={15} /> YOUR ACTIVITY</span>
            <h2 id="recent-contractors-heading">Recent Contractors</h2>
          </div>
          <Button href="/recent-contractors" variant="outline-primary" size="sm">View all</Button>
        </div>

        {loading ? (
          <div className="homeowner-discovery-empty"><Spinner animation="border" size="sm" /> Loading recent contractors…</div>
        ) : recent.length ? (
          <Row className="g-3">
            {recent.map((contractor) => (
              <Col md={6} key={contractor.id}>
                <Card className="homeowner-contractor-preview-card h-100">
                  <Card.Body>
                    <div className="homeowner-contractor-preview-card__identity">
                      <ContractorAvatar image={contractor.image} name={contractor.name} />
                      <div><h3>{contractor.name}</h3><p>{contractor.companyName || contractor.specialty || "General Contractor"}</p></div>
                    </div>
                    <div className="homeowner-contractor-preview-card__meta"><MapPin size={14} /> {contractor.location || "Location not provided"}</div>
                    <div className="homeowner-contractor-preview-card__context"><BriefcaseBusiness size={14} /> {contractor.relatedTo?.name || "Recent interaction"}</div>
                    <Button href={`/recent-contractors/${encodeURIComponent(contractor.id)}`} variant="light" size="sm" className="w-100 mt-3">View profile</Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="homeowner-discovery-empty"><UsersRound size={22} /><span>{recentFailed ? "Recent contractors could not be loaded." : "Your recent contractor interactions will appear here."}</span></div>
        )}
      </section>

      <section className="homeowner-discovery-section" aria-labelledby="suggested-contractors-heading">
        <div className="homeowner-discovery-section__header">
          <div>
            <span className="homeowner-discovery-section__kicker is-suggested"><ShieldCheck size={15} /> AGENT RECOMMENDED</span>
            <h2 id="suggested-contractors-heading">Suggested Contractors</h2>
          </div>
          <Button href="/suggested-contractors" variant="outline-primary" size="sm">View all</Button>
        </div>

        {loading ? (
          <div className="homeowner-discovery-empty"><Spinner animation="border" size="sm" /> Loading suggestions…</div>
        ) : suggested.length ? (
          <Row className="g-3">
            {suggested.map((contractor) => (
              <Col md={6} key={contractor.id}>
                <Card className="homeowner-contractor-preview-card is-suggested h-100">
                  <Card.Body>
                    <div className="homeowner-contractor-preview-card__identity">
                      <ContractorAvatar image={contractor.image} name={contractor.name} />
                      <div><h3>{contractor.name}</h3><p>{contractor.companyName || contractor.profile}</p></div>
                    </div>
                    <div className="homeowner-contractor-preview-card__meta"><MapPin size={14} /> {contractor.location}</div>
                    <div className="homeowner-contractor-preview-card__context"><ShieldCheck size={14} /> Suggested by {contractor.agentName}</div>
                    <div className="homeowner-contractor-preview-card__tags">
                      {(contractor.services.length ? contractor.services : [contractor.profile]).slice(0, 3).map((service) => <Badge bg="light" text="dark" key={service}>{service}</Badge>)}
                    </div>
                    <Button href="/suggested-contractors" variant="light" size="sm" className="w-100 mt-3">View details</Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="homeowner-discovery-empty"><ShieldCheck size={22} /><span>{suggestedFailed ? "Suggested contractors could not be loaded." : "Contractors recommended by your agent will appear here."}</span></div>
        )}
      </section>
    </div>
  );
}
